import type { ProblemDetails } from '@/api/types';

/**
 * Base URL for all Mission Control API requests. In dev, Vite proxies
 * `/api` to `http://127.0.0.1:8080`. In production the frontend is served
 * from the same FastAPI origin so the relative path just works.
 *
 * Paths may be passed to the helper with or without the `/api` prefix:
 *   - `/api/agents`     → unchanged
 *   - `/agents`         → automatically prefixed with `/api`
 *   - `http(s)://...`   → unchanged (rare; for cross-origin fetch)
 */
export const API_BASE = '/api';

/**
 * Structured error thrown by the API helper for any non-2xx response.
 * Carries the RFC 9457 Problem Details so callers can show
 * `title`/`detail` directly without re-parsing. `traceId` is the
 * `X-Trace-Id` header echoed from the backend (§5).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail?: string;
  readonly type?: string;
  readonly traceId?: string;
  readonly problem?: ProblemDetails;

  constructor(
    status: number,
    title: string,
    detail?: string,
    type?: string,
    traceId?: string,
    problem?: ProblemDetails,
  ) {
    super(detail ? `${title}: ${detail}` : title);
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.type = type;
    this.traceId = traceId;
    this.problem = problem;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body' | 'method'> {
  /** Request body — objects are JSON-serialized; strings/FormData pass through. */
  body?: unknown;
  /** Override method; rarely needed — use the verb helpers instead. */
  method?: string;
  /** Sugar for `headers: { 'If-Match': etag }`. */
  ifMatch?: string;
  /** Skip automatic JSON serialization of the body. */
  rawBody?: boolean;
  /** Parse the response body? Defaults to true. */
  parse?: boolean;
}

export interface ApiResponseWithMeta<T> {
  data: T;
  etag?: string;
  traceId?: string;
  status: number;
  headers: Headers;
}

const JSON_METHODS = new Set(['POST', 'PUT', 'PATCH']);

function resolveUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith(API_BASE + '/') || path === API_BASE) return path;
  if (path.startsWith('/')) return `${API_BASE}${path}`;
  return `${API_BASE}/${path}`;
}

/**
 * Core fetch wrapper. Returns the full envelope (data + headers + etag)
 * so `api.getWithMeta` can surface ETags for optimistic concurrency. The
 * thin `apiCall` wrapper below unwraps this to just `T` for the common
 * case.
 */
async function request<T>(path: string, init: ApiRequestInit = {}): Promise<ApiResponseWithMeta<T>> {
  const {
    body,
    ifMatch,
    rawBody,
    headers: initHeaders,
    method: initMethod,
    parse = true,
    ...rest
  } = init;

  const method = (initMethod ?? (body != null ? 'POST' : 'GET')).toUpperCase();
  const headers = new Headers(initHeaders ?? {});

  let requestBody: BodyInit | null | undefined;
  if (body == null) {
    requestBody = undefined;
  } else if (rawBody) {
    requestBody = body as BodyInit;
  } else if (JSON_METHODS.has(method)) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    requestBody = JSON.stringify(body);
  } else {
    requestBody = body as BodyInit;
  }

  if (ifMatch) headers.set('If-Match', ifMatch);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const url = resolveUrl(path);

  const response = await fetch(url, {
    ...rest,
    method,
    headers,
    credentials: 'include',
    body: requestBody,
  });

  const traceId = response.headers.get('X-Trace-Id') ?? undefined;
  const etag = response.headers.get('ETag') ?? undefined;

  if (response.status === 204 || response.status === 205) {
    return {
      data: undefined as T,
      etag,
      traceId,
      status: response.status,
      headers: response.headers,
    };
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  const isJson =
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json');

  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    if (isJson) {
      try {
        problem = (await response.json()) as ProblemDetails;
      } catch {
        problem = undefined;
      }
    }
    const title = problem?.title ?? response.statusText ?? 'Request failed';
    const detail = problem?.detail;
    const type = problem?.type;
    throw new ApiError(response.status, title, detail, type, traceId, problem);
  }

  if (!parse) {
    return {
      data: undefined as T,
      etag,
      traceId,
      status: response.status,
      headers: response.headers,
    };
  }

  if (!isJson) {
    const text = await response.text();
    return {
      data: text as unknown as T,
      etag,
      traceId,
      status: response.status,
      headers: response.headers,
    };
  }

  const data = (await response.json()) as T;
  return { data, etag, traceId, status: response.status, headers: response.headers };
}

/**
 * Primary entry point: `api<T>(path, init?)` returns the parsed payload.
 *
 * Also carries verb helpers on itself:
 *   - `api.get<T>(path, init?)` — explicit GET, same as `api<T>(path)`
 *   - `api.post<T>(path, body?, init?)` — POST + JSON body
 *   - `api.put<T>(path, body?, init?)` — PUT + JSON body
 *   - `api.patch<T>(path, body?, init?)` — PATCH + JSON body
 *   - `api.delete<T>(path, init?)` — DELETE
 *   - `api.getWithMeta<T>(path, init?)` — returns `{ data, etag, ... }`
 *     (useful for If-Match flows)
 *   - `api.request<T>(path, init?)` — low-level, returns the full envelope
 */
async function apiCall<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const res = await request<T>(path, init);
  return res.data;
}

interface ApiWithVerbs {
  <T = unknown>(path: string, init?: ApiRequestInit): Promise<T>;
  get: <T = unknown>(path: string, init?: ApiRequestInit) => Promise<T>;
  getWithMeta: <T = unknown>(path: string, init?: ApiRequestInit) => Promise<ApiResponseWithMeta<T>>;
  post: <T = unknown>(path: string, body?: unknown, init?: ApiRequestInit) => Promise<T>;
  put: <T = unknown>(path: string, body?: unknown, init?: ApiRequestInit) => Promise<T>;
  patch: <T = unknown>(path: string, body?: unknown, init?: ApiRequestInit) => Promise<T>;
  delete: <T = unknown>(path: string, init?: ApiRequestInit) => Promise<T>;
  request: <T = unknown>(path: string, init?: ApiRequestInit) => Promise<ApiResponseWithMeta<T>>;
}

const api = apiCall as ApiWithVerbs;

api.get = <T = unknown>(path: string, init: ApiRequestInit = {}) =>
  apiCall<T>(path, { ...init, method: 'GET' });

api.getWithMeta = <T = unknown>(path: string, init: ApiRequestInit = {}) =>
  request<T>(path, { ...init, method: 'GET' });

api.post = <T = unknown>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiCall<T>(path, { ...init, method: 'POST', body });

api.put = <T = unknown>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiCall<T>(path, { ...init, method: 'PUT', body });

api.patch = <T = unknown>(path: string, body?: unknown, init: ApiRequestInit = {}) =>
  apiCall<T>(path, { ...init, method: 'PATCH', body });

api.delete = <T = unknown>(path: string, init: ApiRequestInit = {}) =>
  apiCall<T>(path, { ...init, method: 'DELETE' });

api.request = request;

export { api };
